import { useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";

import { getSupabaseClient } from "../providers/supabase/supabase";

type MfaSmsEnrollProps = {
  onEnrolled: (factorId: string) => void;
};

export const MfaSmsEnroll = ({ onEnrolled }: MfaSmsEnrollProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [step, setStep] = useState<"phone" | "code">("phone");

  const sendChallenge = async (id: string) => {
    const { data: challenge, error: challengeError } =
      await getSupabaseClient().auth.mfa.challenge({ factorId: id });
    if (challengeError) throw challengeError;
    setChallengeId(challenge.id);
  };

  const handlePhoneSubmit: SubmitHandler<FieldValues> = async (values) => {
    setLoading(true);
    try {
      const phone = String(values.phone ?? "").trim();
      const { data, error } = await getSupabaseClient().auth.mfa.enroll({
        factorType: "phone",
        friendlyName: translate("crm.mfa.sms.friendly_name", {
          _: "SMS",
        }),
        phone,
      });

      if (error) {
        throw error;
      }

      setFactorId(data.id);
      // Challenge is what actually sends the SMS via Twilio.
      await sendChallenge(data.id);
      setStep("code");
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : translate("crm.mfa.enroll_error", {
              _: "Could not send SMS code. Check Auth Twilio env (GOTRUE_SMS_*) and phone number.",
            });
      notify(message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!factorId) return;
    setLoading(true);
    try {
      await sendChallenge(factorId);
      notify(
        translate("crm.mfa.sms.code_resent", { _: "A new code was sent." }),
        { type: "info" },
      );
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : translate("crm.mfa.enroll_error", {
              _: "Could not resend SMS code. Please try again.",
            });
      notify(message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit: SubmitHandler<FieldValues> = async (values) => {
    if (!factorId || !challengeId) return;

    setLoading(true);
    try {
      const { error: verifyError } = await getSupabaseClient().auth.mfa.verify({
        factorId,
        challengeId,
        code: values.code,
      });

      if (verifyError) {
        throw verifyError;
      }

      onEnrolled(factorId);
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.verify_error", {
        type: "error",
        messageArgs: {
          _: "Invalid code. Please try again.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (step === "phone") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {translate("crm.mfa.sms.description", {
            _: "Receive one-time codes via SMS. Use international format, e.g. +4512345678.",
          })}
        </p>
        <Form onSubmit={handlePhoneSubmit}>
          <TextInput
            source="phone"
            label="crm.mfa.sms.phone"
            validate={required()}
            helperText={false}
            autoComplete="tel"
          />
          <Button type="submit" className="w-full mt-4" disabled={loading}>
            {translate("crm.mfa.sms.send_code", { _: "Send code" })}
          </Button>
        </Form>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {translate("crm.mfa.sms.code_sent", {
          _: "Enter the code we sent to your phone.",
        })}
      </p>
      <Form onSubmit={handleCodeSubmit}>
        <TextInput
          source="code"
          label="crm.mfa.code"
          validate={required()}
          helperText={false}
          autoComplete="one-time-code"
        />
        <Button type="submit" className="w-full mt-4" disabled={loading}>
          {translate("crm.mfa.verify_and_continue", {
            _: "Verify and continue",
          })}
        </Button>
      </Form>
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        disabled={loading || !factorId}
        onClick={() => void handleResend()}
      >
        {translate("crm.mfa.sms.resend", { _: "Resend code" })}
      </Button>
    </div>
  );
};
